import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { RequestsFormValues } from '../../models';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { FieldError } from './FieldError';
import { useProtocol } from '../../../context';

type MaterialRow = {
  label: string;
  dateKey: keyof RequestsFormValues['nrl_other'];
  noteKey: keyof RequestsFormValues['nrl_other'];
  nameKey?: keyof RequestsFormValues['nrl_other'];
};

const MATERIALS: MaterialRow[] = [
  {
    label: 'Výtěr nos+krk',
    dateKey: 'swab_nose_throat_collected_at',
    noteKey: 'swab_nose_throat_note'
  },
  {
    label: 'Endotrach. stěr/aspirát/sputum',
    dateKey: 'endotracheal_swab_aspirate_sputum_collected_at',
    noteKey: 'endotracheal_swab_aspirate_sputum_note'
  },
  { label: 'BAL', dateKey: 'bal_collected_at', noteKey: 'bal_note' },
  { label: 'Likvor', dateKey: 'csf_collected_at', noteKey: 'csf_note' },
  { label: 'Biopsie', dateKey: 'biopsy_collected_at', noteKey: 'biopsy_note' },
  {
    label: 'Krev plná srážlivá',
    dateKey: 'full_blood_clot_collected_at',
    noteKey: 'full_blood_clot_note'
  },
  { label: 'Sliny', dateKey: 'saliva_collected_at', noteKey: 'saliva_note' },
  { label: 'Sekční stěry', dateKey: 'autopsy_swabs_collected_at', noteKey: 'autopsy_swabs_note' },
  {
    label: 'Jiný materiál',
    dateKey: 'other_collected_at',
    noteKey: 'other_note',
    nameKey: 'other_material_name'
  }
];

const MAX_NOTE = 255;

export const NRLOtherForm: FC = () => {
  const {
    register,
    formState: { errors },
    setValue,
    getValues
  } = useFormContext<RequestsFormValues>();
  const { protocol } = useProtocol();

  const handleDateFocus = (dateKey: string) => {
    const path = `nrl_other.${dateKey}` as any;
    const current = getValues(path);
    if (!current && protocol?.autopsy_date) {
      setValue(path, `${protocol.autopsy_date}T00:00`);
    }
  };

  return (
    <Wrapper>
      <Grid>
        <HeadCell />
        <HeadCell>Datum a čas odběru</HeadCell>
        <HeadCell>Poznámka</HeadCell>

        {MATERIALS.map((m) => (
          <Row key={m.dateKey as string}>
            <LabelCell>
              {m.nameKey ? (
                <NameRow>
                  <span>{m.label}</span>
                  <NameInput
                    type="text"
                    placeholder="název materiálu"
                    onFocus={() => handleDateFocus(m.dateKey as string)}
                    {...register(`nrl_other.${m.nameKey}` as any)}
                  />
                </NameRow>
              ) : (
                m.label
              )}
            </LabelCell>
            <DateCell>
              <input type="datetime-local" {...register(`nrl_other.${m.dateKey}` as any)} />
            </DateCell>
            <NoteCell>
              <NoteInput
                type="text"
                onFocus={() => handleDateFocus(m.dateKey as string)}
                {...register(`nrl_other.${m.noteKey}` as any, {
                  maxLength: { value: MAX_NOTE, message: `Max ${MAX_NOTE} znaků` }
                })}
              />
              <FieldError error={(errors as any)?.nrl_other?.[m.noteKey]} />
            </NoteCell>
          </Row>
        ))}
      </Grid>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: ${Spacing(2)} 0;
  overflow-x: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 220px 200px 1fr;
  gap: ${Spacing(2)};
  align-items: start;
  min-width: 600px;
`;

const HeadCell = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
  padding-bottom: ${Spacing(1)};
`;

const Row = styled.div`
  display: contents;
`;

const LabelCell = styled.div`
  color: ${Colors.grey};
  padding: ${Spacing(1)} 0;
  display: flex;
  align-items: flex-start;
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
  width: 100%;
`;

const NameInput = styled.input`
  height: 30px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(1)};
  font-size: 13px;
  width: 100%;
`;

const DateCell = styled.div`
  input[type='datetime-local'] {
    height: 32px;
    border: 1px solid ${Colors.lightGrey};
    padding: 0 ${Spacing(1)};
    width: 100%;
  }
`;

const NoteCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NoteInput = styled.input`
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
`;
